import type { CalendarSyncCalendarEvent } from '@cue/calendar-sync';
import { findIanaWindowsMappingFromUnkown } from '@cue/calendars';
import { addDays, addHours, parseISO, subSeconds } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export function getCalendarSyncTimelineEventRange(
  event: Pick<
    CalendarSyncCalendarEvent,
    'isAllDay' | 'startUtc' | 'endUtc' | 'start' | 'end' | 'originalStartTimeZone' | 'originalEndTimeZone'
  >
): { start: Date; end: Date } | null {
  const parseBoundary = (
    utcValue: string | null | undefined,
    boundary: CalendarSyncCalendarEvent['start'],
    originalTimeZone: string | null | undefined
  ): Date | null => {
    const value = utcValue ?? boundary?.dateTime;
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    if (!event.isAllDay) {
      return parsed;
    }

    // All-day boundaries are calendar dates, displayed at local midnight with an exclusive end.
    let date = value.slice(0, 10);
    const timeZone = originalTimeZone || boundary?.timeZone;
    if (utcValue && timeZone) {
      const ianaTimeZone = findIanaWindowsMappingFromUnkown(timeZone)?.iana[0] ?? timeZone;
      try {
        date = formatInTimeZone(parsed, ianaTimeZone, 'yyyy-MM-dd');
      } catch {
        // If the provider's time zone is unknown, preserve the date supplied by the API.
      }
    }

    const midnight = parseISO(date);
    return Number.isNaN(midnight.getTime()) ? null : midnight;
  };

  const start = parseBoundary(event.startUtc, event.start, event.originalStartTimeZone);
  if (!start) {
    return null;
  }

  const parsedEnd = parseBoundary(event.endUtc, event.end, event.originalEndTimeZone || event.originalStartTimeZone);
  const end = parsedEnd && parsedEnd > start ? parsedEnd : event.isAllDay ? addDays(start, 1) : addHours(start, 1);

  return {
    start,
    // The provider's all-day end is an exclusive midnight boundary. Keep the visual range in the previous day.
    end: event.isAllDay ? subSeconds(end, 1) : end,
  };
}
